/**
 * Landing Page component
 * Marketing page for LostMind AI Video Lab
 * Context: First impression and conversion page
 */

import { 
  Container, 
  Title, 
  Text, 
  Button, 
  Stack, 
  Grid, 
  Card, 
  Group,
  Badge,
  List,
  ThemeIcon
} from '@mantine/core';
import { 
  IconVideo, 
  IconBrain, 
  IconChartBar, 
  IconCheck,
  IconArrowRight,
  IconStar
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <Container size="xl" py="xl">
      {/* Hero Section */}
      <Stack ta="center" gap="xl" py={80}>
        <Badge size="lg" variant="gradient" gradient={{ from: 'neural-blue', to: 'electric-cyan' }}>
          LostMind AI Video Lab
        </Badge>
        
        <Title order={1} size={48} ta="center" fw={700}>
          Transform YouTube into Your
          <Text component="span" variant="gradient" gradient={{ from: 'electric-cyan', to: 'warm-gold' }}>
            {' '}Learning Playground
          </Text>
        </Title>
        
        <Text size="xl" color="dimmed" ta="center" maw={600}>
          Turn any YouTube video into interactive data, engaging summaries, and personalized learning apps. 
          Powered by advanced AI to enhance your learning experience.
        </Text>
        
        <Group gap="md">
          <Button 
            size="xl" 
            rightSection={<IconArrowRight size={18} />}
            onClick={() => navigate('/dashboard')}
          >
            Start Creating - Free
          </Button>
          <Button 
            size="xl" 
            variant="outline"
            leftSection={<IconVideo size={18} />}
          >
            Watch Demo
          </Button>
        </Group>
      </Stack>

      {/* Features Grid */}
      <Grid my={80}>
        <Grid.Col span={{ md: 4 }}>
          <Card shadow="md" padding="xl" h="100%">
            <ThemeIcon size={60} radius="md" variant="gradient" gradient={{ from: 'neural-blue', to: 'electric-cyan' }}>
              <IconBrain size={30} />
            </ThemeIcon>
            <Text size="lg" fw={500} mt="md">
              AI-Powered Analysis
            </Text>
            <Text size="sm" color="dimmed" mt="sm">
              Extract key moments, generate excitement charts, and create comprehensive summaries with advanced AI.
            </Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ md: 4 }}>
          <Card shadow="md" padding="xl" h="100%">
            <ThemeIcon size={60} radius="md" variant="gradient" gradient={{ from: 'electric-cyan', to: 'warm-gold' }}>
              <IconChartBar size={30} />
            </ThemeIcon>
            <Text size="lg" fw={500} mt="md">
              Interactive Visualizations
            </Text>
            <Text size="sm" color="dimmed" mt="sm">
              Transform video content into clickable charts, timeline views, and engaging data visualizations.
            </Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ md: 4 }}>
          <Card shadow="md" padding="xl" h="100%">
            <ThemeIcon size={60} radius="md" variant="gradient" gradient={{ from: 'warm-gold', to: 'neural-blue' }}>
              <IconVideo size={30} />
            </ThemeIcon>
            <Text size="lg" fw={500} mt="md">
              Learning App Generation
            </Text>
            <Text size="sm" color="dimmed" mt="sm">
              Generate complete, shareable learning applications from any video with quizzes and interactive elements.
            </Text>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Pricing Section */}
      <Stack ta="center" gap="xl" py={80}>
        <Title order={2} ta="center">
          Flexible Credit System
        </Title>
        <Text size="lg" color="dimmed" ta="center" maw={600}>
          Pay only for what you use. No monthly commitments required.
        </Text>
        
        <Grid maw={800} w="100%">
          <Grid.Col span={{ md: 6 }}>
            <Card shadow="md" padding="xl" h="100%">
              <Text size="lg" fw={500}>Free Starter</Text>
              <Text size="xl" fw={700} mt="sm">10 Credits</Text>
              <Text size="sm" color="dimmed">Perfect for trying out the platform</Text>
              
              <List mt="md" spacing="xs" icon={
                <ThemeIcon size={20} radius="xl" color="green">
                  <IconCheck size={12} />
                </ThemeIcon>
              }>
                <List.Item>10 analysis actions</List.Item>
                <List.Item>2 learning apps</List.Item>
                <List.Item>Basic video support</List.Item>
                <List.Item>Community support</List.Item>
              </List>
              
              <Button fullWidth mt="xl" variant="outline">
                Get Started Free
              </Button>
            </Card>
          </Grid.Col>
          
          <Grid.Col span={{ md: 6 }}>
            <Card shadow="md" padding="xl" h="100%" style={{ position: 'relative' }}>
              <Badge 
                variant="gradient" 
                gradient={{ from: 'electric-cyan', to: 'warm-gold' }}
                style={{ position: 'absolute', top: -10, right: 20 }}
              >
                <IconStar size={12} /> Popular
              </Badge>
              
              <Text size="lg" fw={500}>Pro Monthly</Text>
              <Text size="xl" fw={700} mt="sm">100 Credits/mo</Text>
              <Text size="sm" color="dimmed">$15/month - Best value for regular users</Text>
              
              <List mt="md" spacing="xs" icon={
                <ThemeIcon size={20} radius="xl" color="blue">
                  <IconCheck size={12} />
                </ThemeIcon>
              }>
                <List.Item>100 analysis actions</List.Item>
                <List.Item>20 learning apps</List.Item>
                <List.Item>Long video support</List.Item>
                <List.Item>Priority support</List.Item>
                <List.Item>Advanced sharing</List.Item>
              </List>
              
              <Button fullWidth mt="xl">
                Start Pro Trial
              </Button>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>

      {/* CTA Section */}
      <Stack ta="center" gap="xl" py={80} style={{ 
        background: 'linear-gradient(135deg, rgba(26, 54, 93, 0.1) 0%, rgba(0, 217, 255, 0.1) 100%)',
        borderRadius: '16px',
        padding: '60px 40px'
      }}>
        <Title order={2} ta="center">
          Ready to Transform Your Learning?
        </Title>
        <Text size="lg" color="dimmed" ta="center" maw={600}>
          Join thousands of learners using AI to unlock deeper insights from video content.
        </Text>
        <Button 
          size="xl"
          rightSection={<IconArrowRight size={18} />}
          onClick={() => navigate('/dashboard')}
        >
          Create Your First Project
        </Button>
      </Stack>
    </Container>
  );
}